import {
  createRouter,
  createWebHistory,
  RouteLocationNormalizedGeneric,
} from 'vue-router'
import { createAuthGuard } from '@/src/shared/auth/routerGuard'
import { useVendorStore } from '@/src/vendor/store'
import { useAuthApiStore } from '@/src/shared/auth/authApiStore' // added

const LoginForm = () => import('../../components/auth/LoginForm.vue')
const ForgotPassword = () => import('../../components/auth/ForgotPassword.vue')
const ResetPassword = () => import('../../components/auth/ResetPassword.vue')
const Issues = () => import('../../components/Issues.vue')
const IssueDetails = () => import('../../components/issue/IssueDetails.vue')
const Messages = () => import('../../components/issue/Messages.vue')
const IssueStatus = () => import('../../components/issue/IssueStatus.vue')
const IssueAttachments = () =>
  import('../../components/issue/IssueAttachments.vue')
const IssueWatchList = () => import('../../components/issue/IssueWatchList.vue')
const IssueFindReplace = () =>
  import('../../components/issue/IssueFindReplace.vue')
const UploadAttachment = () => import('../../components/UploadAttachment.vue')
const IssueLayout = () => import('../../components/issue/IssueLayout.vue')
const Answering = () => import('../section/Answering.vue')
const TeamWork = () => import('../../components/section/TeamWork.vue')
const Feedback = () => import('../../components/section/Feedback.vue')
const AnswersImport = () => import('../../components/section/AnswersImport.vue')
const Users = () => import('../Users.vue')
const User = () => import('../User.vue')
const Component404 = () => import('../../components/404.vue')

const issueIdProps = (route: RouteLocationNormalizedGeneric) => ({
  issueId: Number(route.params.issueId),
})

const issueIdSectionIdProps = (route: RouteLocationNormalizedGeneric) => ({
  issueId: Number(route.params.issueId),
  sectionId: Number(route.params.sectionId),
})

const issuePageProps = { issuePage: issueIdProps }
const issuePageSectionProps = { issuePage: issueIdSectionIdProps }

const router = createRouter({
  history: createWebHistory('/vendor/'),
  routes: [
    {
      path: '',
      redirect: 'opportunities',
    },
    {
      path: '/login',
      name: 'Login',
      alias: '/logout',
      component: LoginForm,
      props: {
        appType: 'vendor',
        defaultRedirect: 'opportunities',
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
    },
    {
      path: '/reset',
      name: 'reset-password',
      component: ResetPassword,
      props: (route) => ({
        key: route.query.key,
        user: route.query.user,
      }),
    },
    {
      path: '/opportunities',
      name: 'issues',
      component: Issues,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/users',
      name: 'users',
      component: Users,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/users/:userId',
      name: 'user',
      component: User,
      props: true,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/user/add',
      name: 'addUser',
      component: User,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/i/:issueId',
      name: 'issueHome',
      component: IssueLayout,
      props: issueIdProps,
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: 'details',
          name: 'details',
          components: { issuePage: IssueDetails },
          props: issuePageProps,
        },
        {
          path: 'status',
          name: 'status',
          components: { issuePage: IssueStatus },
          props: issuePageProps,
        },
        {
          path: 'messages',
          name: 'messages',
          components: { issuePage: Messages },
          props: issuePageProps,
        },
        {
          path: 'attachments',
          name: 'attachments',
          components: { issuePage: IssueAttachments },
          props: issuePageProps,
        },
        {
          path: 'upload',
          name: 'upload',
          components: { issuePage: UploadAttachment },
          props: issuePageProps,
        },
        {
          path: 'watchers',
          name: 'watchers',
          components: { issuePage: IssueWatchList },
          props: issuePageProps,
        },
        {
          path: 'findReplace',
          name: 'findReplace',
          components: { issuePage: IssueFindReplace },
          props: issuePageProps,
        },
        {
          path: 's/:sectionId(\\d+)/answer',
          name: 'answer',
          components: { issuePage: Answering },
          props: issuePageSectionProps,
        },
        {
          path: 's/:sectionId(\\d+)/team',
          name: 'team',
          components: { issuePage: TeamWork },
          props: issuePageSectionProps,
        },
        {
          path: 's/:sectionId(\\d+)/feedback',
          name: 'feedback',
          components: { issuePage: Feedback },
          props: issuePageSectionProps,
        },
        {
          path: 's/:sectionId(\\d+)/import',
          name: 'import',
          components: { issuePage: AnswersImport },
          props: issuePageSectionProps,
        },
      ],
    },
    {
      path: '/:patchMatch(.*)*',
      name: '404',
      component: Component404,
    },
  ],
})

// Apply the auth guard with vendor-specific configuration
router.beforeEach(
  createAuthGuard({
    redirectRoute: 'Login',
    authCheckFn: () => {
      const vendorStore = useVendorStore()
      const authApiStore = useAuthApiStore()
      // Allow if already logged in OR if persisted credentials exist (prevents losing deep link on refresh)
      return (
        vendorStore.isLoggedIn ||
        (!!authApiStore.accessToken && !!authApiStore.currentUserId)
      )
    },
  })
)

export default router
